import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DollarSign, Edit2, Lock } from 'lucide-react';

const TABS = [
  { id: 'voice_engine', label: 'Voice engine', note: 'ElevenLabs voice + speech platform, billed per call minute.' },
  { id: 'voice_ai', label: 'Voice AI models', note: 'The AI model that thinks during a call. Billed at ElevenLabs’ live cost + markup. Range = short to long agent prompts.' },
  { id: 'chat', label: 'Chat AI models', note: 'Every AI reply on web chat, SMS, WhatsApp, Instagram and Facebook.' },
  { id: 'usage', label: 'Knowledge Base & SMS', note: 'Usage extras added on top of calls and chats.' },
  { id: 'tools', label: 'Add-on tools', note: 'One-off tools, charged per run. Set by environment, read-only here.' },
];

const fmt = (n) => (n == null ? '—' : `$${Number(n).toFixed(n < 0.01 && n > 0 ? 4 : 3)}`);
const pct = (c, p) => (c > 0 ? Math.round(((p - c) / c) * 1000) / 10 : null);

const Margin = ({ c, p }) => {
  const m = pct(c, p);
  if (m == null) return <span className="text-xs text-gray-500">—</span>;
  const tone = m <= 0 ? 'text-red-400 bg-red-400/10' : m < 50 ? 'text-amber-400 bg-amber-400/10' : 'text-emerald-400 bg-emerald-400/10';
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full tabular-nums ${tone}`}>{m > 0 ? '+' : ''}{m}%</span>;
};

const EditCell = ({ value, onSave, testId, strong, textPrimary, textSecondary, inputClass }) => {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState('');
  const commit = () => { const n = parseFloat(v); setEdit(false); if (!Number.isNaN(n) && n >= 0 && n !== value) onSave(n); };
  if (edit) return <input autoFocus type="number" step="0.001" min="0" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit}
    onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEdit(false); }}
    className={`w-24 px-2 py-1 rounded-md text-sm text-right tabular-nums border ${inputClass}`} data-testid={`${testId}-input`} />;
  return <button type="button" onClick={() => { setV(String(value ?? '')); setEdit(true); }} data-testid={testId}
    className="group inline-flex items-center gap-1.5">
    <span className={`text-sm tabular-nums ${strong ? `font-semibold ${textPrimary}` : textSecondary}`}>{fmt(value)}</span>
    <Edit2 size={11} className={`opacity-0 group-hover:opacity-70 transition-opacity ${textSecondary}`} />
  </button>;
};

const patchFor = (key, n) => {
  const [cat, ...rest] = key.split('_');
  const model = rest.join('_');
  if (cat === 'voice') return { voice: { [model]: n }, orb: { [model]: n } };
  if (cat === 'chat') return { chat: { [model]: n } };
  if (cat === 'sms') return { sms: { [model]: n } };
  return { kb: n };
};

export const BillingRatesPanel = ({ api, isDarkMode, cardClass, textPrimary, textSecondary, inputClass }) => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('voice_engine');
  const load = () => api.get('/api/admin/billing-rates-overview').then((r) => setData(r.data)).catch(() => toast.error('Could not load rates'));
  useEffect(() => { load(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (key, field, n) => {
    try {
      await api.put(`/api/admin/credit-rates/${field}`, { rates: patchFor(key, n) });
      toast.success(field === 'cost' ? 'Cost updated' : 'Client price updated');
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d?.errors?.[0] || (typeof d === 'string' ? d : 'Save failed'));
    }
  };

  const t = TABS.find((x) => x.id === tab);
  const line = isDarkMode ? 'border-white/[0.06]' : 'border-gray-100';
  const head = `px-5 py-3 text-[11px] font-semibold uppercase tracking-wider ${textSecondary}`;
  const cell = { textPrimary, textSecondary, inputClass };
  const rows = data?.[tab] || [];

  return (
    <div className={`${cardClass} rounded-2xl p-6`} data-testid="billing-rates-panel">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-emerald-400/10 flex items-center justify-center"><DollarSign className="text-emerald-400" size={20} /></div>
        <div>
          <h3 className={`font-semibold ${textPrimary}`}>Billing Rates</h3>
          <p className={`text-xs ${textSecondary}`}>What Voxinity pays vs what clients pay · click a price to edit · USD</p>
        </div>
      </div>
      <div className={`inline-flex flex-wrap gap-1 p-1 rounded-xl mb-4 ${isDarkMode ? 'bg-white/[0.04]' : 'bg-gray-100'}`} role="tablist">
        {TABS.map((x) => <button key={x.id} role="tab" onClick={() => setTab(x.id)} data-testid={`rates-tab-${x.id}`}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === x.id ? (isDarkMode ? 'bg-white/10 text-white' : 'bg-white text-gray-900 shadow-sm') : textSecondary}`}>{x.label}</button>)}
      </div>
      <p className={`text-sm mb-4 ${textSecondary}`} data-testid="rates-tab-note">{t.note}{tab === 'voice_ai' && data ? ` Current markup: +${Math.round(data.voice_ai_markup * 100)}%.` : ''}</p>
      {!data ? <p className={`py-10 text-center text-sm ${textSecondary}`}>Loading rates…</p> : (
        <div className={`overflow-x-auto rounded-xl border ${line}`}>
          <table className="w-full text-sm" data-testid={`rates-table-${tab}`}>
            <thead className={isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50'}>
              <tr><th className={`${head} text-left`}>{tab.startsWith('voice_ai') || tab === 'chat' ? 'AI model' : 'Service'}</th>
                <th className={`${head} text-left`}>Billed</th><th className={`${head} text-right`}>Your cost</th>
                <th className={`${head} text-right`}>Client price</th><th className={`${head} text-right`}>Profit</th></tr>
            </thead>
            <tbody>
              {tab === 'voice_ai' ? rows.map((r) => (
                <tr key={r.key} className={`border-t ${line}`} data-testid={`rate-row-${r.key}`}>
                  <td className={`px-5 py-3.5 font-medium ${textPrimary}`}>{r.label}</td>
                  <td className={`px-5 py-3.5 ${textSecondary}`}>per minute</td>
                  <td className={`px-5 py-3.5 text-right tabular-nums ${textSecondary}`}>{fmt(r.cost_low)} – {fmt(r.cost_high)}</td>
                  <td className={`px-5 py-3.5 text-right tabular-nums font-semibold ${textPrimary}`}>{fmt(r.price_low)} – {fmt(r.price_high)}</td>
                  <td className="px-5 py-3.5 text-right"><span className="text-[11px] font-semibold px-2 py-0.5 rounded-full text-amber-400 bg-amber-400/10">+{Math.round(data.voice_ai_markup * 100)}%</span></td>
                </tr>
              )) : rows.map((r) => (
                <tr key={r.key} className={`border-t ${line}`} data-testid={`rate-row-${r.key}`}>
                  <td className={`px-5 py-3.5 font-medium ${textPrimary}`}>{r.label}</td>
                  <td className={`px-5 py-3.5 ${textSecondary}`}>{r.unit}</td>
                  <td className="px-5 py-3.5 text-right">{r.editable ? <EditCell value={r.cost} onSave={(n) => save(r.key, 'cost', n)} testId={`edit-cost-${r.key}`} {...cell} /> : <span className={`tabular-nums ${textSecondary}`}>{fmt(r.cost)}</span>}</td>
                  <td className="px-5 py-3.5 text-right">{r.editable ? <EditCell strong value={r.price} onSave={(n) => save(r.key, 'standard', n)} testId={`edit-price-${r.key}`} {...cell} />
                    : <span className={`inline-flex items-center gap-1.5 tabular-nums font-semibold ${textPrimary}`}>{fmt(r.price)}<Lock size={11} className={textSecondary} /></span>}</td>
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    {r.cost != null && <span className={`text-xs tabular-nums mr-2 ${textSecondary}`}>{fmt(r.price - r.cost)}</span>}<Margin c={r.cost} p={r.price} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BillingRatesPanel;
